import React, { useEffect, useState } from 'react';
import { CurrencyDollar, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Per-show "Where do payments go?" panel for the producer's Show Edit page.
 *
 * Lets the producer flip a single show between routing to their own
 * connected Square account vs the platform's master account. Always
 * editable (user choice 2a — no LIVE/COMPLETE lock-out). Saves
 * immediately on toggle; no separate Save button.
 *
 * Visibility rules:
 *  - When the producer is NOT connected to Square, the section is
 *    rendered with the toggle disabled + a "Connect Square first" CTA.
 *  - When connected, the toggle reflects the show's current
 *    `square_routing_enabled` flag and writes through to /shows/{id}/square-routing.
 */
export const ShowSquareRoutingPanel = ({ showId, initialRouted, initialMerchantId }) => {
  const [enabled, setEnabled] = useState(!!initialRouted);
  const [merchantId, setMerchantId] = useState(initialMerchantId || null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/connect/square/status')
      .then(({ data }) => setConnectStatus(data))
      .catch(() => setConnectStatus({ connected: false }));
  }, []);

  const connected = !!connectStatus?.connected;

  const handleToggle = async (next) => {
    if (next && !connected) {
      toast.error('Producer must connect their Square account first');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/shows/${showId}/square-routing`, { enabled: next });
      setEnabled(!!data.square_routing_enabled);
      setMerchantId(data.square_merchant_id || null);
      toast.success(next ? 'Payments now route to the producer\'s Square account' : 'Payments now route to the master account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-xl p-5 sm:p-6" data-testid="show-square-routing-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-500/15' : 'bg-[#FF9500]/15'}`}>
          <CurrencyDollar size={20} weight="duotone" className={enabled ? 'text-emerald-400' : 'text-[#FF9500]'} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit' }}>Payment Routing</h3>
          <p className="text-xs text-[#9E9E9E] mt-1">
            Where should rider payments for this show land?
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => handleToggle(false)}
          disabled={saving}
          className={`text-left rounded-lg border p-4 transition-all ${!enabled ? 'border-[#FF9500] bg-[#FF9500]/5' : 'border-[#1F1F1F] hover:border-[#262626] bg-[#0A0A0A]'}`}
          data-testid="route-master-btn"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-white">Master Account</span>
            {!enabled && <CheckCircle size={16} weight="fill" className="text-[#FF9500]" />}
          </div>
          <p className="text-[11px] text-[#9E9E9E]">The Show Master receives funds; you reconcile out of band.</p>
        </button>
        <button
          type="button"
          onClick={() => handleToggle(true)}
          disabled={saving || !connected}
          className={`text-left rounded-lg border p-4 transition-all ${enabled ? 'border-emerald-500 bg-emerald-500/5' : !connected ? 'border-[#1F1F1F] bg-[#0A0A0A] opacity-60 cursor-not-allowed' : 'border-[#1F1F1F] hover:border-[#262626] bg-[#0A0A0A]'}`}
          data-testid="route-producer-btn"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-white">Producer's Square Account</span>
            {enabled && <CheckCircle size={16} weight="fill" className="text-emerald-400" />}
          </div>
          <p className="text-[11px] text-[#9E9E9E]">
            {connected
              ? `Direct deposits to ${merchantId || connectStatus?.merchant_id || 'the producer\u2019s Square account'}. Platform takes service fee.`
              : 'Producer must connect Square on their Dashboard to enable this.'}
          </p>
        </button>
      </div>

      {!connected && (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-[#FF9500]">
          <Warning size={14} weight="fill" />
          <span>Producer must connect their Square account on the Dashboard to route payments directly.</span>
        </div>
      )}
    </div>
  );
};
